import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, type OnDestroy, Output } from '@angular/core';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type { WorkPosition } from '../../../shared/models/api/work-position.model';
import { WorkPositionService } from '../../../shared/services/api/work-position.service';

@Component({
  selector: 'opa-work-position-delete',
  imports: [CommonModule, PorscheDesignSystemModule],
  templateUrl: './work-position-delete.component.html',
  styleUrl: './work-position-delete.component.scss',
})
export class WorkPositionDeleteComponent implements OnDestroy {
  private readonly workPositionService = inject(WorkPositionService);
  private readonly toastManager = inject(ToastManager);

  @Input({ required: true }) open = false;
  @Input({ required: true }) workPosition: WorkPosition | undefined = undefined;
  @Output() dismiss = new EventEmitter<WorkPosition | undefined>();

  private readonly subSink = new SubSink();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(workPosition?: WorkPosition): void {
    this.dismiss.emit(workPosition);
  }

  submit(): void {
    const workPosition = this.workPosition;
    if (!workPosition) return;

    this.submitting.next(true);
    this.subSink.sink = this.workPositionService
      .deleteWorkPosition(workPosition.id)
      .pipe(
        tap({
          next: () => {
            this.toastManager.addMessage({
              text: `Work Position ${workPosition.description} deleted`,
              state: 'success',
            });
            this.submitting.next(false);
            this.dismiss.emit(workPosition);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }
}
